import {
  isAuthenticationError,
  isAuthorizationError,
  isDeltaBaseError,
  isEventStoreAlreadyExistsError,
  isEventStoreNotFoundError,
  isIllegalStateError,
  isInternalServerError,
  isInvalidSubscriptionConfigError,
  isNotFoundError,
  isRateLimitError,
  isSerializationError,
  isStorageError,
  isStreamNotFoundError,
  isStreamVersionConflictError,
  isSubscriptionNotFoundError,
  isTimeoutError,
  isValidationError,
  isVersionConflictError,
} from '@delta-base/toolkit';
import type { WideEventsWrapper } from './wide-events';

/**
 * Configuration for error categorization
 */
export interface ErrorCategorizationConfig {
  /** Additional expected error type guards specific to the handler */
  expectedErrorGuards?: Array<(error: unknown) => boolean>;
  /** Custom error type guards for domain-specific errors */
  customExpectedErrorGuards?: Array<(error: unknown) => boolean>;
}

/**
 * Default expected error type guards that are common across handlers
 * These are all DeltaBase errors that are considered "expected" business/system errors
 * rather than unexpected programming errors
 */
export const DEFAULT_EXPECTED_ERROR_GUARDS = [
  // DeltaBase errors
  isDeltaBaseError,

  // DeltaBase errors that are considered "expected" business/system errors
  // rather than unexpected programming errors
  isIllegalStateError,
  isValidationError,
  isVersionConflictError,
  isStreamVersionConflictError,

  // Resource not found errors
  isEventStoreNotFoundError,
  isSubscriptionNotFoundError,
  isStreamNotFoundError,
  isNotFoundError,

  // Authentication & authorization errors
  isAuthenticationError,
  isAuthorizationError,

  // Rate limiting & resource constraints
  isRateLimitError,
  isEventStoreAlreadyExistsError,
  isInvalidSubscriptionConfigError,

  // Infrastructure & system errors
  isTimeoutError,
  isStorageError,
  isSerializationError,
  isInternalServerError,
];

/**
 * Handles error tracking and categorization for command handlers
 *
 * This function provides intelligent error categorization by:
 * - Distinguishing between expected and unexpected errors
 * - Tracking errors with appropriate context
 * - Supporting custom error type guards for domain-specific errors
 *
 * @param error - The error that occurred
 * @param tracker - Wide events wrapper for tracking (optional)
 * @param config - Configuration for error categorization
 * @returns Never (always throws the error)
 *
 * @example
 * ```typescript
 * try {
 *   await someOperation();
 * } catch (error) {
 *   handleAndTrackError(error, tracker, {
 *     expectedErrorGuards: [isUserNotFoundError]
 *   });
 * }
 * ```
 */
export function handleAndTrackError(
  error: unknown,
  tracker?: WideEventsWrapper,
  config: ErrorCategorizationConfig = {}
): never {
  let errorType: string;
  let unexpected: boolean;

  if (error instanceof Error) {
    errorType = error.constructor.name;

    // Combine default expected error guards with handler-specific ones
    const expectedErrorGuards = [
      ...DEFAULT_EXPECTED_ERROR_GUARDS,
      ...(config.expectedErrorGuards || []),
      ...(config.customExpectedErrorGuards || []),
    ];

    // Check if error is expected using type guards
    unexpected = !expectedErrorGuards.some((guard) => guard(error));
  } else {
    errorType = 'UnknownError';
    unexpected = true;
  }

  // Track error with appropriate context
  tracker?.trackError({
    type: errorType,
    message: error instanceof Error ? error.message : String(error),
    unexpected,
  });

  // Record error event
  tracker?.recordEvent('handler.error', {
    'error.type': errorType,
    'error.message': error instanceof Error ? error.message : String(error),
    'error.unexpected': unexpected,
  });

  throw error;
}

/**
 * Type guard factory for creating domain-specific error guards
 *
 * @param ErrorClass - The error class to check for
 * @returns Type guard function
 *
 * @example
 * ```typescript
 * class UserNotFoundError extends Error {}
 * const isUserNotFoundError = createErrorGuard(UserNotFoundError);
 *
 * handleAndTrackError(error, tracker, {
 *   expectedErrorGuards: [isUserNotFoundError]
 * });
 * ```
 */
export function createErrorGuard<T extends Error>(
  ErrorClass: new (...args: any[]) => T
): (error: unknown) => error is T {
  return (error: unknown): error is T => {
    return error instanceof ErrorClass;
  };
}

/**
 * Utility function to determine if an error should be considered expected
 *
 * @param error - The error to check
 * @param additionalGuards - Additional type guards to check
 * @returns true if the error is expected, false if unexpected
 *
 * @example
 * ```typescript
 * const isExpected = isExpectedError(error, [isUserNotFoundError]);
 * console.log(isExpected ? 'Expected error' : 'Unexpected error');
 * ```
 */
export function isExpectedError(
  error: unknown,
  additionalGuards: Array<(error: unknown) => boolean> = []
): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const allGuards = [...DEFAULT_EXPECTED_ERROR_GUARDS, ...additionalGuards];
  return allGuards.some((guard) => guard(error));
}

/**
 * Utility function to categorize errors for metrics and monitoring
 *
 * @param error - The error to categorize
 * @param additionalGuards - Additional type guards for expected errors
 * @returns Error category information
 *
 * @example
 * ```typescript
 * const category = categorizeError(error);
 * console.log(`Error category: ${category.category}, type: ${category.type}`);
 * ```
 */
export function categorizeError(
  error: unknown,
  additionalGuards: Array<(error: unknown) => boolean> = []
): {
  category: 'expected' | 'unexpected';
  type: string;
  message: string;
} {
  const type = error instanceof Error ? error.constructor.name : 'UnknownError';
  const message = error instanceof Error ? error.message : String(error);
  const category = isExpectedError(error, additionalGuards)
    ? 'expected'
    : 'unexpected';

  return { category, type, message };
}
